import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { refusalText, isRefusal } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { LineRow, SmeCardView } from "../../../api/types";
import { showToast } from "../../../ui/toastStore";
import type { AnswerKind } from "./decide";
import { cardQueryKey } from "./useCardDisplay";

/** What was just decided on the whole card, so the page can move on. */
export type Settled = "validated" | "sent_back" | "rejected";

export interface AnswerTarget {
  kind: AnswerKind;
  /** The line the words are about, or "" for the whole card. */
  lineId: string;
}

interface Options {
  /** The whole card was validated, sent back or rejected. */
  onSettled: (what: Settled, card: SmeCardView) => void;
}

/**
 * The SME's answers on one card, with their refusals in plain words. Validate, Send back and Reject change a state;
 * Note changes none and is never refused. A refusal because the card changed or is being edited reads the card again.
 */
export function useCardAnswers(card: SmeCardView, { onSettled }: Options) {
  const queryClient = useQueryClient();
  const validate = useApiCommand("validate");
  const sendBack = useApiCommand("send_back");
  const reject = useApiCommand("reject");
  const validateLine = useApiCommand("validate_line");
  const rejectLine = useApiCommand("reject_line");
  const addNote = useApiCommand("add_note");
  const [target, setTarget] = useState<AnswerTarget | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const element = card.element;
  const busy = [validate, sendBack, reject, validateLine, rejectLine, addNote].some((command) => command.isPending);
  const lineOf = (lineId: string): LineRow | undefined => [...card.library_lines, ...card.lines].find((line) => line.id === lineId);

  const failed = (error: unknown) => {
    setProblem(refusalText(error));
    if (isRefusal(error, "stale", "locked", "blocked")) void queryClient.invalidateQueries({ queryKey: ["sme_card"] });
  };

  const stored = (view: SmeCardView) => {
    queryClient.setQueryData(cardQueryKey(element.id), view);
    void queryClient.invalidateQueries({ queryKey: ["inbox"] });
  };

  const close = () => {
    setTarget(null);
    setProblem(null);
  };

  const validateElement = async () => {
    setProblem(null);
    try {
      const reply = await validate.mutateAsync({ id: element.id, expected_version: element.version });
      stored(reply.data);
      showToast({ text: `Validated: ${element.name}.` });
      onSettled("validated", reply.data);
    } catch (error) {
      failed(error);
    }
  };

  const validateOneLine = async (line: LineRow) => {
    setProblem(null);
    try {
      const reply = await validateLine.mutateAsync({ id: line.id, expected_version: line.version });
      stored(reply.data);
      showToast({ text: "Line validated." });
    } catch (error) {
      failed(error);
    }
  };

  const confirm = async (text: string, lineId: string) => {
    if (!target) return;
    setProblem(null);
    const line = lineId ? lineOf(lineId) : undefined;
    try {
      if (target.kind === "send_back") {
        const reply = await sendBack.mutateAsync({
          id: element.id,
          expected_version: element.version,
          comment: text,
          ...(line ? { line_id: line.id, ...(line.kind === "pick_value" && line.code ? { value_code: line.code } : {}) } : {}),
        });
        stored(reply.data);
        close();
        showToast({ text: line ? "The line went back to the engineer with your comment." : `Sent back: ${element.name}.` });
        if (!line) onSettled("sent_back", reply.data);
      } else if (target.kind === "reject") {
        if (line) {
          const reply = await rejectLine.mutateAsync({ id: line.id, expected_version: line.version, reason: text });
          stored(reply.data);
          close();
          showToast({ text: "Line rejected." });
        } else {
          const reply = await reject.mutateAsync({ id: element.id, expected_version: element.version, reason: text });
          stored(reply.data);
          close();
          showToast({ text: `Rejected: ${element.name}.` });
          onSettled("rejected", reply.data);
        }
      } else {
        await addNote.mutateAsync({ element_id: element.id, ...(line ? { line_id: line.id } : {}), kind: "sme_note", text });
        void queryClient.invalidateQueries({ queryKey: ["sme_card"] });
        void queryClient.invalidateQueries({ queryKey: ["inbox"] });
        close();
        showToast({ text: "Note added. The card still waits for your answer." });
      }
    } catch (error) {
      failed(error);
    }
  };

  /** A line's own button: Validate and Reject act on the line; Send back and Note ask for words first. */
  const openLine = (kind: AnswerKind | "validate", line: LineRow) => {
    if (kind === "validate") {
      void validateOneLine(line);
      return;
    }
    setProblem(null);
    setTarget({ kind, lineId: line.id });
  };

  const openElement = (kind: AnswerKind) => {
    setProblem(null);
    setTarget({ kind, lineId: "" });
  };

  return { busy, target, problem, validateElement, openElement, openLine, confirm, close };
}
